"use client"

import { useState } from "react"
import type { StorageIntegration } from "@/schema"
import { Button, Label, Switch } from "@ui/shad"
import { ChevronRight, File, Folder, Link2 } from "lucide-react"

import { useStoragePickerPages } from "@/hooks/api/storage/pages"
import { InformationDialog } from "@/components/dialogs"

import { StoragePageList } from "./storage-page-list"

export const STORAGE_PROVIDER_NAMES: Record<StorageIntegration["type"], string> = {
  "StorageIntegration::Google": "Google Drive",
  "StorageIntegration::GoogleBackup": "Google Drive",
  "StorageIntegration::Microsoft": "OneDrive",
  "StorageIntegration::Box": "Box",
  "StorageIntegration::Dropbox": "Dropbox",
  "StorageIntegration::ShareFile": "ShareFile",
  "StorageIntegration::Egnyte": "Egnyte",
}

export interface StorageLinkSelection {
  external_id: string
  name: string
  mime_type?: string | null
  kind: "file" | "folder"
  web_url: string
  mode: "reference" | "copy_per_instance"
}

interface StorageLinkPickerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  integration: StorageIntegration
  // Templates can mint a fresh copy of the file per spawned instance instead of linking
  allowCopyPerInstance?: boolean
  onAttach: (selection: StorageLinkSelection) => void
  isAttaching?: boolean
}

export function StorageLinkPickerDialog(props: StorageLinkPickerDialogProps) {
  return props.open ? <StorageLinkPickerContent key={props.integration.id} {...props} /> : null
}

function StorageLinkPickerContent({
  onOpenChange,
  integration,
  allowCopyPerInstance = false,
  onAttach,
  isAttaching = false,
}: StorageLinkPickerDialogProps) {
  const [currentFolderId, setCurrentFolderId] = useState<string | undefined>(undefined)
  const [folderPath, setFolderPath] = useState<{ id: string | undefined; name: string }[]>([
    { id: undefined, name: "Root" },
  ])
  const [mode, setMode] = useState<"reference" | "copy_per_instance">("reference")

  const providerName = STORAGE_PROVIDER_NAMES[integration.type]
  const page = useStoragePickerPages({
    integrationId: integration.id,
    folderId: currentFolderId,
    bookId: integration.book_id,
  })

  function navigateToFolder(id: string, name: string) {
    setCurrentFolderId(id)
    setFolderPath((prev) => [...prev, { id, name }])
  }

  function navigateBack(index: number) {
    const target = folderPath[index]
    setCurrentFolderId(target.id)
    setFolderPath((prev) => prev.slice(0, index + 1))
  }

  function attach(item: { id: string; name: string; mime_type?: string; web_url?: string }, kind: "file" | "folder") {
    if (!item.web_url) return

    onAttach({
      external_id: item.id,
      name: item.name,
      mime_type: item.mime_type,
      kind,
      web_url: item.web_url,
      mode: kind === "file" ? mode : "reference",
    })
  }

  return (
    <InformationDialog dismiss="explicit" title={<>Attach from {providerName}</>} open onOpenChange={onOpenChange}>
      <div className="space-y-2">
        <div className="flex items-center gap-1 text-sm">
          {folderPath.map((entry, i) => (
            <span key={i} className="flex items-center gap-1">
              {i > 0 && <ChevronRight className="text-muted-foreground size-3" />}
              <button
                type="button"
                onClick={() => navigateBack(i)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                {entry.name}
              </button>
            </span>
          ))}
        </div>

        <StoragePageList
          key={currentFolderId ?? "root"}
          page={page}
          isItemFocusable={(file) => file.type === "folder" || (!!file.web_url && !isAttaching)}
        >
          {(folder) =>
            folder.type === "folder" ? (
              <div key={folder.id} className="group flex h-10 items-center gap-1">
                <button
                  type="button"
                  onClick={() => navigateToFolder(folder.id, folder.name)}
                  className="hover:bg-muted flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm"
                >
                  <Folder className="text-muted-foreground size-4 shrink-0" />
                  <span className="truncate">{folder.name}</span>
                </button>
                <Button
                  variant="ghost"
                  size="xs"
                  className="shrink-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                  disabled={!folder.web_url || isAttaching}
                  onClick={() => attach(folder, "folder")}
                >
                  <Link2 />
                  Attach folder
                </Button>
              </div>
            ) : (
              <button
                key={folder.id}
                type="button"
                disabled={!folder.web_url || isAttaching}
                title={folder.web_url ? undefined : "This file cannot be linked"}
                onClick={() => attach(folder, "file")}
                className="hover:bg-muted flex h-10 w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm disabled:cursor-not-allowed disabled:opacity-40"
              >
                <File className="text-muted-foreground size-4 shrink-0" />
                <span className="truncate">{folder.name}</span>
              </button>
            )
          }
        </StoragePageList>

        {allowCopyPerInstance && (
          <div className="flex items-center gap-2 pt-4">
            <Switch
              id="storage-link-mode-copy"
              checked={mode === "copy_per_instance"}
              onCheckedChange={(checked) => setMode(checked ? "copy_per_instance" : "reference")}
            />
            <Label htmlFor="storage-link-mode-copy" className="text-sm font-normal">
              Duplicate into linked household folder
            </Label>
          </div>
        )}
      </div>
    </InformationDialog>
  )
}
